import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../../../supabaseClient';
import {
  getActiveServiceForUser, getProfileName, getVehicleById,
  getDefaultVehicle, setDriverOnTrip
} from '../../../services/api/trips-ui';
import { useAuth } from '../../USER/Auth/AuthContext';
import { updateServiceStatus, getServiceTrackingToken, getServiceConfirmationToken } from '../services/deliveryServices';
import { passengerRespondCounterOffer } from '../../../services/api/trips';
import ReviewModal from '../Review/ReviewModal';
import TrackingMap from '../Map/TrackingMap';
import LiveChat from '../Chat/LiveChat';
import DeliveryEvidence from './DeliveryEvidence';
import PaymentPanel from '../Payment/PaymentPanel';
import { Html5Qrcode } from 'html5-qrcode';
import Swal from 'sweetalert2';
import { notifyServiceAssigned, notifyServiceStatusChange } from '../../../services/pushNotifications';
import NavigationView from '../Navigation/NavigationView';
import PanicButton from '../../UI/PanicButton';
import { SERVICE_STATUSES, VEHICLE_TYPES, SERVICE_TYPES } from '../../../constants';
import { shouldAutoOpenChat, shouldShowCounterOffer } from './utils/realtimeGuards';
import { BryModal } from '../../UI/BryUI';

import {
  Bike, Car, MapPin, CheckCircle, XCircle, Loader2, Flag, Map,
  MessageSquare, Camera, Share2, QrCode, Navigation, CreditCard
} from 'lucide-react';

const statusConfig = {
  [SERVICE_STATUSES.SEARCHING]: {
    label: 'Buscando conductor',
    color: 'var(--bry-accent)',
    icon: Loader2,
    next: null,
    nextLabel: null,
    progress: 10,
    spin: true
  },
  [SERVICE_STATUSES.DRIVER_ASSIGNED]: { 
    label: 'Conductor asignado', 
    color: '#0EABB0', 
    icon: Loader2, 
    next: SERVICE_STATUSES.IN_PROGRESS, 
    nextLabel: 'Iniciar viaje',
    progress: 25,
    spin: true
  },
  [SERVICE_STATUSES.IN_PROGRESS]: { 
    label: 'Viaje en curso', 
    color: 'var(--bry-accent)', 
    icon: Bike, 
    next: SERVICE_STATUSES.AWAITING_CONFIRMATION, 
    nextLabel: 'Finalizar viaje',
    progress: 50,
    spin: false
  },
  [SERVICE_STATUSES.AWAITING_CONFIRMATION]: { 
    label: 'Esperando confirmación', 
    color: '#0EABB0', 
    icon: QrCode, 
    next: null, 
    nextLabel: null,
    progress: 75,
    spin: false
  },
  [SERVICE_STATUSES.COMPLETED]: { 
    label: 'Viaje completado', 
    color: '#84cc16', 
    icon: CheckCircle, 
    next: null, 
    nextLabel: null,
    progress: 100,
    spin: false
  },
  [SERVICE_STATUSES.CANCELLED]: { 
    label: 'Cancelado', 
    color: '#ef4444', 
    icon: XCircle, 
    next: null, 
    nextLabel: null,
    progress: 0,
    spin: false
  },
};

function ServiceTracker() {
  const { currentUser, isDriver, isPassenger } = useAuth();
  const [activeService, setActiveService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [evidenceType, setEvidenceType] = useState('dropoff');
  const [trackingToken, setTrackingToken] = useState(null);
  const [confirmationToken, setConfirmationToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [searchElapsed, setSearchElapsed] = useState(0);
  const qrScannerRef = useRef(null);
  const prevStatusRef = useRef(null);
  const lastShownOfferKeyRef = useRef(null);

  useEffect(() => {
    if (activeService?.status) {
      prevStatusRef.current = activeService.status;
    }
  }, [activeService?.status]);

  useEffect(() => {
    if (!activeService || activeService.status !== SERVICE_STATUSES.SEARCHING || !isPassenger) {
      setSearchElapsed(0);
      return;
    }

    const calcElapsed = () => {
      const created = activeService.created_at ? new Date(activeService.created_at).getTime() : Date.now();
      const diffSec = Math.floor((Date.now() - created) / 1000);
      setSearchElapsed(Math.max(0, diffSec));
    };

    calcElapsed();
    const interval = setInterval(calcElapsed, 5000);
    return () => clearInterval(interval);
  }, [activeService?.service_id, activeService?.status, activeService?.created_at, isPassenger]);

  const startScanner = () => {
    setScanning(true);
    setTimeout(() => {
      try {
        const html5QrCode = new Html5Qrcode("reader");
        qrScannerRef.current = html5QrCode;
        html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
          },
          async (decodedText) => {
            let token = decodedText;
            if (decodedText.includes('/confirmar/')) {
              token = decodedText.split('/confirmar/')[1].split(/[?#]/)[0];
            }
            await stopScanner();
            
            setUpdating(true);
            try {
              const { confirmServiceByToken } = await import('../services/deliveryServices');
              const result = await confirmServiceByToken(token);
              if (result.success) {
                setShowReviewModal(true);
                Swal.fire({
                  icon: 'success',
                  title: 'Servicio Confirmado',
                  text: 'El viaje ha finalizado exitosamente. Por favor califica a tu conductor.',
                  confirmButtonColor: '#4CAF50'
                });
              } else {
                Swal.fire('Error', result.error || 'No se pudo confirmar el servicio', 'error');
              }
            } catch (err) {
              Swal.fire('Error', 'Error al procesar la confirmación', 'error');
            } finally {
              setUpdating(false);
            }
          },
          () => {}
        ).catch(err => {
          console.error("Error starting Html5Qrcode", err);
          Swal.fire('Permisos de cámara', 'No se pudo iniciar la cámara. Revisa los permisos e inténtalo de nuevo.', 'error');
          setScanning(false);
        });
      } catch (err) {
        console.error("Html5Qrcode init error", err);
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (qrScannerRef.current && qrScannerRef.current.isScanning) {
      try {
        await qrScannerRef.current.stop();
      } catch (err) {
        console.error("Error stopping scanner", err);
      }
    }
    qrScannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      if (qrScannerRef.current && qrScannerRef.current.isScanning) {
        qrScannerRef.current.stop().catch(err => console.error(err));
      }
    };
  }, []);

  useEffect(() => {
    if (!activeService?.service_id) return;
    if (activeService.type === SERVICE_TYPES.DELIVERY) {
      getServiceTrackingToken(activeService.service_id).then(t => setTrackingToken(t));
    }
    if (activeService.status === SERVICE_STATUSES.AWAITING_CONFIRMATION) {
      getServiceConfirmationToken(activeService.service_id).then(t => setConfirmationToken(t));
    } else {
      setConfirmationToken(null);
    }
  }, [activeService?.service_id, activeService?.type, activeService?.status]);

  const handleShareTracking = () => {
    if (!trackingToken) return;
    const link = `${window.location.origin}/track/${trackingToken}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePassengerCounterOfferDecision = async (accepted) => {
    if (!activeService?.service_id || !currentUser?.id) return;
    setUpdating(true);
    try {
      const response = await passengerRespondCounterOffer(activeService.service_id, currentUser.id, accepted);
      if (response?.success) {
        Swal.fire({
          icon: 'success',
          title: accepted ? 'Contraoferta aceptada' : 'Servicio cancelado',
          text: accepted ? 'El viaje iniciará pronto' : 'El servicio ha sido cancelado',
          confirmButtonColor: '#10B981',
          background: '#1F2937',
          color: '#F9FAFB'
        });
        if (!accepted) {
          setTimeout(() => setActiveService(null), 1500);
        } else {
          setActiveService(prev => prev ? ({ ...prev, counter_offer_by: null }) : prev);
        }
      }
    } catch (err) {
      Swal.fire('Error', err.message || 'Error procesando respuesta', 'error');
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchActive = async () => {
      const roleField = isDriver ? 'driver_uid' : 'user_uid';
      const { data, error } = await getActiveServiceForUser(currentUser.id, roleField);

      if (!error && data) {
        let driverName = 'Conductor asignado';
        let driverVehicle = null;

        if (data.driver_uid) {
          const { data: profileData } = await getProfileName(data.driver_uid);

          if (profileData && profileData.name) {
            driverName = profileData.name;
          }

          if (data.vehicle_id) {
            const { data: vehData } = await getVehicleById(data.vehicle_id);
            if (vehData) driverVehicle = vehData;
          } else {
            const { data: vehData } = await getDefaultVehicle(data.driver_uid);
            if (vehData) driverVehicle = vehData;
          }
        }

        const enrichedData = {
          ...data,
          driver_name: driverName,
          driver_vehicle: driverVehicle
        };
        setActiveService(enrichedData);
      }
      setLoading(false);
    };

    fetchActive();

    const handleServiceUpdatedEvent = () => {
      fetchActive();
    };
    window.addEventListener('bryemma-service-updated', handleServiceUpdatedEvent);

    const filterField = isDriver ? 'driver_uid' : 'user_uid';
    const sub = supabase
      .channel('service-updates')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'services',
        filter: `${filterField}=eq.${currentUser.id}`
      }, payload => {
        if (payload.new && [SERVICE_STATUSES.SEARCHING, SERVICE_STATUSES.DRIVER_ASSIGNED, SERVICE_STATUSES.IN_PROGRESS, SERVICE_STATUSES.AWAITING_CONFIRMATION, SERVICE_STATUSES.COMPLETED, SERVICE_STATUSES.CANCELLED].includes(payload.new.status)) {
          const cleanUpdate = Object.fromEntries(
            Object.entries(payload.new).filter(([, v]) => v != null)
          );
          setActiveService(prev => {
            const updated = { ...prev, ...cleanUpdate };
            if (prev?.confirmation_token && !cleanUpdate.confirmation_token) {
              updated.confirmation_token = prev.confirmation_token;
            }
            if (prev?.tracking_token && !cleanUpdate.tracking_token) {
              updated.tracking_token = prev.tracking_token;
            }
            return updated;
          });
          if (payload.new.status === SERVICE_STATUSES.COMPLETED) {
            setShowReviewModal(true);
            setShowPaymentModal(false);
          } else if (payload.new.status === SERVICE_STATUSES.AWAITING_CONFIRMATION && isPassenger) {
            setShowPaymentModal(true);
          } else if (shouldAutoOpenChat(prevStatusRef.current, payload.new.status)) {
            setShowChat(true);
          } else if (payload.new.status === SERVICE_STATUSES.CANCELLED) {
            setTimeout(() => setActiveService(null), 5000);
          }
          // Detectar contraoferta del conductor (solo pasajero)
          const offerCheck = (isPassenger && payload.new.status === SERVICE_STATUSES.DRIVER_ASSIGNED)
            ? shouldShowCounterOffer(lastShownOfferKeyRef.current, payload.new)
            : { show: false };

          if (offerCheck.show) {
            lastShownOfferKeyRef.current = offerCheck.key;
            Swal.fire({
              title: '💰 Contraoferta del conductor',
              html: `
                <div style="text-align:left">
                  <p style="color:#9CA3AF">El conductor te ofrece un nuevo precio:</p>
                  <p style="font-size:24px;font-weight:bold;color:#10B981;margin:12px 0">$${payload.new.offered_price}</p>
                  <p style="color:#9CA3AF;font-size:12px;text-decoration:line-through">Original: $${payload.new.price}</p>
                  <p style="color:#F59E0B;font-size:11px;margin-top:12px">Si rechazas, el servicio se cancelará automáticamente.</p>
                </div>
              `,
              showDenyButton: true,
              confirmButtonColor: '#10B981',
              denyButtonColor: '#EF4444',
              confirmButtonText: 'Aceptar',
              denyButtonText: 'Rechazar',
              allowOutsideClick: false,
              allowEscapeKey: false,
              background: '#1F2937',
              color: '#F9FAFB'
            }).then(async (result) => {
              await handlePassengerCounterOfferDecision(Boolean(result.isConfirmed));
            });
          }
        }
        if (payload.eventType === 'UPDATE' && payload.new && payload.old?.status !== payload.new.status) {
          if (isPassenger && payload.new.status === SERVICE_STATUSES.DRIVER_ASSIGNED) {
            notifyServiceAssigned(payload.new, currentUser.id);
          }
          if ([SERVICE_STATUSES.COMPLETED, SERVICE_STATUSES.CANCELLED].includes(payload.new.status)) {
            const otherUid = isDriver ? payload.new.user_uid : payload.new.driver_uid;
            if (otherUid) notifyServiceStatusChange(payload.new, otherUid, payload.new.status);
          }
        }
      })
      .subscribe();

    return () => {
      window.removeEventListener('bryemma-service-updated', handleServiceUpdatedEvent);
      supabase.removeChannel(sub);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isDriver, isPassenger]);

  const handleStatusChange = async (newStatus) => {
    if (!activeService || !isDriver) return;

    if (activeService.type === SERVICE_TYPES.DELIVERY && newStatus === SERVICE_STATUSES.AWAITING_CONFIRMATION) {
      if (!activeService.dropoff_evidence_url) {
        Swal.fire({
          icon: 'warning',
          title: 'Evidencia Obligatoria',
          text: 'Para finalizar una entrega de paquete, debes subir una foto de evidencia.',
          confirmButtonColor: 'var(--bry-accent)',
        });
        setEvidenceType('dropoff');
        setShowEvidence(true);
        return;
      }
    }

    setUpdating(true);
    try {
      await updateServiceStatus(activeService.service_id, newStatus);
      
      if (newStatus === SERVICE_STATUSES.AWAITING_CONFIRMATION) {
        Swal.fire({
          icon: 'info',
          title: 'Esperando confirmación',
          text: 'Muestra el código QR al cliente para que confirme el servicio.',
          confirmButtonColor: '#9C27B0',
        });
      } else if (newStatus === SERVICE_STATUSES.IN_PROGRESS) {
        await setDriverOnTrip(currentUser.id);
      }
    } catch (err) {
      Swal.fire('Error', 'No se pudo actualizar el estado.', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDriverCancelAssigned = async () => {
    Swal.fire({
      title: '¿No puedes realizar el viaje?',
      text: 'Puedes liberar el viaje para que otro conductor cercano lo atienda, o cancelarlo por completo.',
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: '🔄 Liberar para la flota',
      denyButtonText: '❌ Cancelar viaje',
      cancelButtonText: 'Regresar',
      confirmButtonColor: 'var(--bry-accent)',
      denyButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        try {
          const { error } = await supabase
            .from('services')
            .update({ status: SERVICE_STATUSES.SEARCHING, driver_uid: null })
            .eq('service_id', activeService.service_id);
          if (error) throw error;
          await supabase
            .from('drivers_tracking')
            .upsert({ driver_uid: currentUser.id, status: 'online', last_updated: new Date().toISOString() });
          setActiveService(null);
          Swal.fire({
            icon: 'success',
            title: 'Viaje Liberado',
            text: 'El servicio ha vuelto a estar disponible para la flota.',
            timer: 2500,
            showConfirmButton: false,
          });
        } catch (err) {
          Swal.fire('Error', err.message || 'No se pudo liberar el viaje', 'error');
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        handleStatusChange(SERVICE_STATUSES.CANCELLED);
      }
    });
  };

  if (loading || !activeService) return null;

  const config = statusConfig[activeService.status] || statusConfig[SERVICE_STATUSES.DRIVER_ASSIGNED];

  const theme = {
    textMuted: isDriver ? 'text-gray-400' : 'text-gray-500',
    textPrimary: isDriver ? 'text-white' : 'text-gray-900',
  };

  const hasPendingDriverCounterOffer = Boolean(
    isPassenger &&
    activeService.status === SERVICE_STATUSES.DRIVER_ASSIGNED &&
    activeService.offered_price &&
    activeService.counter_offer_by === 'driver'
  );

  return (
    <div
      className="relative w-full rounded-2xl border overflow-hidden mb-4 shadow-lg"
      style={{
        backgroundColor: 'var(--bry-bg-card, #111827)',
        color: 'var(--bry-text-primary)',
        borderColor: 'var(--bry-border-default, rgba(148,163,184,0.2))'
      }}
    >
      {showMap && (
        <div style={{ height: '210px', width: '100%', position: 'relative' }}>
          <TrackingMap service={activeService} height="210px" />
        </div>
      )}

      <div className="p-4 relative z-10">
        {/* Banner visible de Contraoferta del Conductor (Aceptar / Rechazar siempre accesibles) */}
        {hasPendingDriverCounterOffer && (
          <div
            className="mb-4 rounded-xl border p-3"
            style={{
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              borderColor: 'rgba(245, 158, 11, 0.45)',
            }}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-extrabold uppercase tracking-wider text-amber-400">
                💰 Contraoferta del conductor
              </span>
              <span className="text-lg font-black text-emerald-400 tabular-nums">
                ${activeService.offered_price}
              </span>
            </div>
            <p className="text-xs text-gray-300 m-0 mb-3">
              Tarifa original: <span className="line-through text-gray-400">${activeService.price}</span>. ¿Aceptas el nuevo monto?
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={updating}
                onClick={() => handlePassengerCounterOfferDecision(false)}
                style={{
                  minHeight: 44,
                  backgroundColor: 'rgba(239, 68, 68, 0.18)',
                  borderColor: 'rgba(239, 68, 68, 0.45)',
                  color: '#FCA5A5',
                }}
                className="flex-1 rounded-xl border px-3 text-xs font-extrabold"
              >
                Rechazar
              </button>
              <button
                type="button"
                disabled={updating}
                onClick={() => handlePassengerCounterOfferDecision(true)}
                style={{
                  minHeight: 44,
                  backgroundColor: '#0EABB0',
                  color: '#FFFFFF',
                }}
                className="flex-1 rounded-xl border-0 px-3 text-xs font-black shadow-sm"
              >
                Aceptar (${activeService.offered_price})
              </button>
            </div>
          </div>
        )}

        {/* Status Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <div 
              className="flex justify-center items-center rounded-full mr-3 shadow-sm w-11 h-11"
              style={{ backgroundColor: `${config.color}20`, color: config.color }}
            >
              <config.icon 
                size={22}
                className={config.spin ? 'animate-spin' : ''} 
              />
            </div>
            <div>
              <h5 className={`font-bold mb-0 text-base ${theme.textPrimary}`}>{config.label}</h5>
              <span className="bry-badge bry-badge-accent inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[var(--bry-accent)] text-white">
                {activeService.type === SERVICE_TYPES.DELIVERY ? 'Entrega' : 'Viaje'}
              </span>
            </div>
          </div>
          <div className="text-right">
            <h4 className="font-bold mb-0 text-xl text-[var(--bry-accent)]">
              {activeService.offered_price ? (
                <>
                  <span className="line-through text-gray-500 text-xs mr-1">
                    ${activeService.price}
                  </span>
                  ${activeService.offered_price}
                </>
              ) : (
                `$${activeService.price}`
              )}
            </h4>
            <span className={`text-[11px] font-bold ${theme.textMuted}`}>{activeService.distance} km</span>
            {[SERVICE_STATUSES.IN_PROGRESS, SERVICE_STATUSES.AWAITING_CONFIRMATION, SERVICE_STATUSES.COMPLETED].includes(activeService.status) && (
              <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full text-[9px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">
                🔒 Tarifa confirmada
              </span>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between mb-1.5">
            <span className="text-[10px] font-bold tracking-wider" style={{ color: 'var(--bry-text-muted)' }}>PROGRESO DEL SERVICIO</span>
            <span className="text-xs font-bold" style={{ color: config.color }}>{config.progress}%</span>
          </div>
          <div className="bry-progress-bar h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bry-bg-secondary)' }}>
            <div 
              className="bry-progress-fill h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${config.progress}%`, backgroundColor: config.color }}
            ></div>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="flex justify-around mb-4 rounded-xl p-2 border" style={{ backgroundColor: 'var(--bry-bg-secondary)', borderColor: 'var(--bry-bg-secondary)' }}>
          <button type="button" onClick={() => setShowChat(true)} className="flex flex-col items-center justify-center p-1.5 flex-1 hover:opacity-80 transition-opacity">
            <div className="rounded-full flex justify-center items-center mb-1 w-10 h-10 shadow-sm" style={{ backgroundColor: 'var(--bry-bg-primary)' }}>
              <MessageSquare style={{ color: 'var(--bry-accent)' }} size={17} />
            </div>
            <span className="text-xs font-semibold" style={{ color: 'var(--bry-text-muted)' }}>Chat</span>
          </button>
          
          <button type="button" onClick={() => setShowMap(!showMap)} className="flex flex-col items-center justify-center p-1.5 flex-1 hover:opacity-80 transition-opacity">
            <div className="rounded-full flex justify-center items-center mb-1 w-10 h-10 shadow-sm" style={{ backgroundColor: 'var(--bry-bg-primary)' }}>
              <Map style={{ color: 'var(--bry-accent)' }} size={17} />
            </div>
            <span className="text-xs font-semibold" style={{ color: 'var(--bry-text-muted)' }}>{showMap ? 'Ocultar mapa' : 'Ver mapa'}</span>
          </button>
          
          {isDriver && [SERVICE_STATUSES.DRIVER_ASSIGNED, SERVICE_STATUSES.IN_PROGRESS].includes(activeService.status) && (
            <button type="button" onClick={() => setShowNavigation(true)} className="flex flex-col items-center justify-center p-1.5 flex-1 hover:opacity-80 transition-opacity">
              <div className="rounded-full flex justify-center items-center mb-1 w-10 h-10 shadow-sm" style={{ backgroundColor: 'var(--bry-bg-primary)' }}>
                <Navigation style={{ color: 'var(--bry-accent)' }} size={17} />
              </div>
              <span className="text-xs font-semibold" style={{ color: 'var(--bry-text-muted)' }}>Ruta</span>
            </button>
          )}
        </div>

        {/* Driver Info for Passenger */}
        {isPassenger && activeService.driver_name && (
          <div className="flex items-center p-3 mb-4 rounded-xl border" style={{ backgroundColor: 'var(--bry-bg-secondary)', borderColor: 'var(--bry-bg-secondary)' }}>
            <div className="rounded-full flex justify-center items-center text-white font-bold shadow-sm mr-3 w-11 h-11 text-base shrink-0" style={{ backgroundColor: 'var(--bry-bg-primary)' }}>
              {activeService.driver_name.charAt(0).toUpperCase()}
            </div>
            <div className="overflow-hidden">
              <span className="block text-[10px] font-bold tracking-wider" style={{ color: 'var(--bry-text-muted)' }}>CONDUCTOR ASIGNADO</span>
              <span className="font-bold text-sm truncate block">{activeService.driver_name}</span>
              {activeService.driver_vehicle && (
                <div className="mt-1 flex items-center text-xs" style={{ color: 'var(--bry-text-muted)' }}>
                  {activeService.driver_vehicle.vehicle_type === VEHICLE_TYPES.CARRO || activeService.driver_vehicle.vehicle_type === 'car' ? (
                    <Car size={14} className="mr-1.5 shrink-0" />
                  ) : (
                    <Bike size={14} className="mr-1.5 shrink-0" />
                  )}
                  <span className="truncate">
                    {activeService.driver_vehicle.brand} {activeService.driver_vehicle.plate ? `• ${activeService.driver_vehicle.plate}` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Addresses */}
        <div className="relative pl-2 mb-4">
          <div className="absolute left-[19px] top-[24px] w-[2px] h-[calc(100%-48px)]" style={{ backgroundColor: 'var(--bry-bg-secondary)' }}></div>
          
          <div className="flex items-start mb-3 relative">
            <div className="rounded-full flex justify-center items-center z-10 mt-1 shadow-sm w-6 h-6 bg-cyan-500/20 text-cyan-500">
              <div className="rounded-full w-2 h-2 bg-cyan-500"></div>
            </div>
            <div className="ml-3">
              <span className="block text-[10px] font-bold tracking-wider" style={{ color: 'var(--bry-text-muted)' }}>ORIGEN</span>
              <span className="font-bold text-sm">{activeService.pickup?.address || 'Ubicación actual'}</span>
            </div>
          </div>
          
          <div className="flex items-start relative">
            <div className="rounded-full flex justify-center items-center z-10 mt-1 shadow-sm w-6 h-6 bg-pink-500/20 text-pink-500">
              <MapPin size={14} />
            </div>
            <div className="ml-3">
              <span className="block text-[10px] font-bold tracking-wider" style={{ color: 'var(--bry-text-muted)' }}>DESTINO</span>
              <span className="font-bold text-sm">{activeService.dropoff?.address || 'Ubicación seleccionada'}</span>
            </div>
          </div>
        </div>

        {/* Payment Panel */}
        <div className="border-t pt-3 mt-3" style={{ borderColor: 'var(--bry-bg-secondary)' }}>
          <PaymentPanel service={activeService} onRefresh={() => {}} />
        </div>

        {/* Boton P2P Modal para Pasajero */}
        {isPassenger && [SERVICE_STATUSES.IN_PROGRESS, SERVICE_STATUSES.AWAITING_CONFIRMATION].includes(activeService.status) && (
          <div className="mt-3">
            <button
              type="button"
              className="w-full py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 bg-[var(--bry-accent)] text-white hover:bg-[#0c959a] shadow-lg transition-all text-sm"
              onClick={() => setShowPaymentModal(true)}
            >
              <CreditCard size={18} />
              💳 Pagar con Pago Móvil / Binance P2P
            </button>
          </div>
        )}

        {/* Tracking Link */}
        {activeService.type === SERVICE_TYPES.DELIVERY && trackingToken && (
          <div className="mt-3">
            <button
              type="button"
              className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center transition-all border"
              style={{
                backgroundColor: copied ? 'rgba(74, 222, 128, 0.1)' : 'var(--bry-bg-secondary)',
                color: copied ? '#4ade80' : 'var(--bry-accent)',
                borderColor: copied ? '#22c55e' : 'var(--bry-bg-secondary)'
              }}
              onClick={handleShareTracking}
            >
              <Share2 size={18} className="mr-2" />
              {copied ? '¡Enlace copiado!' : 'Compartir enlace de seguimiento'}
            </button>
          </div>
        )}

        {/* Driver Actions */}
        {isDriver && (
          <div className="mt-4 flex flex-col gap-2.5">
            {activeService.status === SERVICE_STATUSES.DRIVER_ASSIGNED && (
              <>
                <button
                  type="button"
                  className="w-full py-3 px-4 rounded-xl font-bold flex items-center justify-center transition-all bg-[var(--bry-accent)] text-white hover:bg-[#0c959a] shadow-md text-sm"
                  onClick={() => setShowNavigation(true)}
                >
                  <Navigation size={18} className="mr-2" /> 🧭 Modo Conducción GPS (Google Maps)
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    style={{
                      minHeight: 46,
                      backgroundColor: 'rgba(239, 68, 68, 0.16)',
                      borderColor: 'rgba(239, 68, 68, 0.45)',
                      color: '#FCA5A5',
                    }}
                    className="flex-1 py-3 px-4 rounded-xl border font-bold flex items-center justify-center transition-all text-sm"
                    onClick={handleDriverCancelAssigned}
                    disabled={updating}
                  >
                    <XCircle size={18} className="mr-2" /> Cancelar
                  </button>
                  <button
                    type="button"
                    style={{ minHeight: 46, backgroundColor: '#0EABB0', color: '#FFFFFF' }}
                    className="flex-1 py-3 px-4 rounded-xl font-bold flex items-center justify-center transition-all shadow-md text-sm"
                    onClick={() => handleStatusChange(config.next)}
                    disabled={updating}
                  >
                    {updating ? <Loader2 size={18} className="animate-spin" /> : <><Flag size={18} className="mr-2" /> {config.nextLabel}</>}
                  </button>
                </div>
              </>
            )}

            {activeService.status === SERVICE_STATUSES.IN_PROGRESS && (
              <>
                {activeService.type === SERVICE_TYPES.DELIVERY && (
                  <button
                    type="button"
                    className="w-full py-3 rounded-xl font-bold flex items-center justify-center transition-all bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 text-sm"
                    onClick={() => {
                      setEvidenceType(!activeService.pickup_evidence_url ? 'pickup' : 'dropoff');
                      setShowEvidence(true);
                    }}
                    disabled={updating}
                  >
                    <Camera size={18} className="mr-2" /> 
                    {!activeService.pickup_evidence_url ? 'Foto Recolección' : 'Foto Entrega'}
                  </button>
                )}
                <button
                  type="button"
                  style={{ minHeight: 46, backgroundColor: '#0EABB0', color: '#FFFFFF' }}
                  className="w-full py-3 rounded-xl font-bold flex items-center justify-center transition-all shadow-md text-sm"
                  onClick={() => setShowNavigation(true)}
                >
                  <Navigation size={18} className="mr-2" /> Navegar al destino
                </button>
                <button
                  type="button"
                  style={{ minHeight: 48 }}
                  className="w-full py-3.5 rounded-xl font-bold flex items-center justify-center transition-all bg-lime-500 text-black hover:bg-lime-600 shadow-md text-base tracking-wide"
                  onClick={() => handleStatusChange(SERVICE_STATUSES.AWAITING_CONFIRMATION)}
                  disabled={updating}
                >
                  {updating ? <Loader2 size={22} className="animate-spin" /> : <><CheckCircle size={22} className="mr-2" /> Viaje Completado</>}
                </button>
              </>
            )}

            {activeService.status === SERVICE_STATUSES.AWAITING_CONFIRMATION && (
              <div className="bry-card text-center mt-2 bg-gray-800 p-4 rounded-2xl border border-gray-700">
                <p className="font-bold mb-3 text-white flex items-center justify-center gap-2 text-sm">
                  <QrCode size={20} className="text-[var(--bry-accent)]" />
                  Muestra este código al cliente
                </p>
                {confirmationToken && (
                  <div className="inline-block p-3 bg-white rounded-xl shadow-lg border-2 border-dashed border-[var(--bry-accent)]">
                    <QRCodeSVG
                      value={`${window.location.origin}/confirmar/${confirmationToken}`}
                      size={170}
                      level="M"
                      includeMargin
                    />
                  </div>
                )}
                <p className="text-gray-400 mt-3 mb-0 text-xs">
                  El cliente debe escanear para confirmar el servicio
                </p>
              </div>
            )}
          </div>
        )}

        {/* Passenger Alerts & Actions */}
        {isPassenger && activeService.status === SERVICE_STATUSES.SEARCHING && (
          <div className="mt-4 flex flex-col gap-2.5">
            {searchElapsed >= 180 && (
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex flex-col gap-2">
                <div className="flex items-center gap-2 font-bold text-sm text-amber-400">
                  <Loader2 size={16} className="animate-spin" />
                  <span>La búsqueda está tomando más de lo habitual</span>
                </div>
                <p className="text-gray-300 m-0">
                  No hemos encontrado un conductor disponible de inmediato. Puedes renovar la búsqueda en el radar de conductores o cancelar la solicitud.
                </p>
                <button
                  type="button"
                  className="w-full py-2.5 px-3 rounded-lg font-bold text-xs bg-[var(--bry-accent)] text-white hover:bg-[#0c959a] transition-all flex items-center justify-center gap-1.5"
                  onClick={async () => {
                    setUpdating(true);
                    try {
                      const { resetSearchingTimeout } = await import('../../../services/api/trips-ui');
                      await resetSearchingTimeout(activeService.service_id);
                      setActiveService(prev => ({ ...prev, created_at: new Date().toISOString() }));
                      setSearchElapsed(0);
                      Swal.fire({
                        icon: 'success',
                        title: 'Radar Renovado',
                        text: 'Tu solicitud vuelve a estar visible en el radar de todos los conductores.',
                        timer: 2500,
                        showConfirmButton: false,
                      });
                    } catch (err) {
                      Swal.fire('Error', 'No se pudo renovar la búsqueda', 'error');
                    } finally {
                      setUpdating(false);
                    }
                  }}
                  disabled={updating}
                >
                  <Flag size={14} /> Renovar Búsqueda en Radar
                </button>
              </div>
            )}

            <button
              type="button"
              style={{
                minHeight: 46,
                backgroundColor: 'rgba(239, 68, 68, 0.14)',
                borderColor: 'rgba(239, 68, 68, 0.4)',
                color: '#EF4444',
              }}
              className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center transition-all border"
              onClick={() => {
                Swal.fire({
                  title: '¿Cancelar solicitud?',
                  text: 'Se cancelará tu solicitud de servicio actual.',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: '#d33',
                  cancelButtonColor: '#aaa',
                  confirmButtonText: 'Sí, cancelar',
                  cancelButtonText: 'No, mantener'
                }).then(async (result) => {
                  if (result.isConfirmed) {
                    setUpdating(true);
                    try {
                      await updateServiceStatus(activeService.service_id, SERVICE_STATUSES.CANCELLED);
                      setActiveService(null);
                    } catch (err) {
                      Swal.fire('Error', 'No se pudo cancelar la solicitud.', 'error');
                    } finally {
                      setUpdating(false);
                    }
                  }
                });
              }}
              disabled={updating}
            >
              {updating ? <Loader2 size={18} className="animate-spin" /> : <><XCircle size={18} className="mr-2" /> Cancelar Solicitud</>}
            </button>
          </div>
        )}

        {isPassenger && activeService.status === SERVICE_STATUSES.IN_PROGRESS && (
          <div className="bry-banner info mt-4 rounded-xl py-3 px-4 flex items-center" style={{ backgroundColor: 'rgba(14,171,176,0.1)', color: 'var(--bry-accent)', border: '1px solid rgba(14,171,176,0.3)' }}>
            <Bike className="mr-3 shrink-0" size={20} />
            <span className="font-medium text-sm">Tu conductor está en camino con tu pedido.</span>
          </div>
        )}

        {isPassenger && [SERVICE_STATUSES.DRIVER_ASSIGNED, SERVICE_STATUSES.IN_PROGRESS].includes(activeService.status) && (
          <div className="mt-3">
            <PanicButton serviceId={activeService.service_id} />
          </div>
        )}

        {isPassenger && activeService.status === SERVICE_STATUSES.AWAITING_CONFIRMATION && (
          <div className="text-center mt-4">
            <div className="bry-banner info bg-cyan-50 border border-cyan-100 rounded-xl p-3 mb-3 flex items-center gap-3">
              <QrCode size={22} className="text-cyan-600 shrink-0" />
              <span className="font-bold text-cyan-800 text-xs text-left">
                Espera a que el conductor te muestre el código QR para escanear
              </span>
            </div>
            
            {scanning ? (
              <div className="mt-2 bg-gray-900 p-3 rounded-2xl shadow-lg text-center relative">
                <div id="reader" className="w-full min-h-[220px] rounded-xl overflow-hidden bg-black"></div>
                <button type="button" className="mt-3 py-2.5 px-6 rounded-full bg-gray-700 text-white font-semibold text-sm hover:bg-gray-600" onClick={stopScanner}>
                  Cancelar Escaneo
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  style={{ minHeight: 48, backgroundColor: '#0EABB0', color: '#FFFFFF' }}
                  className="w-full py-3.5 rounded-xl font-bold flex items-center justify-center transition-all shadow-lg text-base"
                  onClick={startScanner}
                >
                  <QrCode size={20} className="mr-2" />
                  Escanear QR de Confirmación
                </button>
                
                {confirmationToken && (
                  <button
                    type="button"
                    className="text-gray-400 underline text-xs mt-1 hover:text-gray-200 font-semibold py-2"
                    onClick={async () => {
                      try {
                        const { confirmServiceByToken } = await import('../services/deliveryServices');
                        const result = await confirmServiceByToken(confirmationToken);
                        if (result.success) {
                          setShowReviewModal(true);
                        } else {
                          Swal.fire('Error', result.error || 'No se pudo confirmar', 'error');
                        }
                      } catch (err) {
                        Swal.fire('Error', 'No se pudo confirmar el servicio', 'error');
                      }
                    }}
                  >
                    Confirmar manualmente sin cámara
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ReviewModal
        show={showReviewModal}
        onHide={() => {
          setShowReviewModal(false);
          setActiveService(null);
        }}
        service={activeService}
        reviewType={isDriver ? 'driver_to_customer' : 'customer_to_driver'}
      />

      <LiveChat 
        show={showChat} 
        onHide={() => setShowChat(false)} 
        serviceId={activeService?.service_id} 
        service={activeService}
      />

      <DeliveryEvidence
        show={showEvidence}
        onHide={() => setShowEvidence(false)}
        serviceId={activeService?.service_id}
        type={evidenceType}
        onUploadComplete={(url, type) => {
          setActiveService(prev => ({
            ...prev,
            [type === 'pickup' ? 'pickup_evidence_url' : 'dropoff_evidence_url']: url
          }));
        }}
      />

      {/* Modal P2P de Pago Móvil / Binance */}
      <BryModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Pago Móvil / Binance P2P"
        size="lg"
      >
        <PaymentPanel service={activeService} onRefresh={() => {}} />
      </BryModal>

      <AnimatePresence>
        {showNavigation && (
          <NavigationView
            service={activeService}
            isDriver={isDriver}
            onClose={() => setShowNavigation(false)}
            onStatusChange={(status) => handleStatusChange(status)}
            updating={updating}
            confirmationToken={confirmationToken}
            onOpenChat={() => setShowChat(true)}
            onCancel={handleDriverCancelAssigned}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default ServiceTracker;
